// External imports
import React, { useState, useEffect, useRef } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { onIdTokenChanged } from 'firebase/auth';
import Modal from 'react-modal';
import { saveAs } from 'file-saver';
import html2pdf from 'html2pdf.js';

// Firebase and Context imports
import { auth } from './firebase';
import { useTheme } from './context/ThemeContext';
import { EncryptionProvider } from './context/EncryptionContext';
import { ThemeProvider } from './context/ThemeContext';
import { log, logError, logWarning } from './utils/logger';
import { sanitizeNoteHtml } from './utils/sanitizeHtml';
import { noteHtmlToMarkdown, serializeNotesToMarkdownV2, serializeNotesToMarkdownV3 } from './utils/markdownNotes';
import { 
  subscribeToUserTags, 
  subscribeToUserCategories, 
  updateUserCategories,
  initializeNewUser,
  importNotesAtomically,
  subscribeToNotes
} from './firebase/notes';

// Component imports
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import ContentArea from './components/ContentArea';
import TagManager from './components/TagManager';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import LandingPage from './components/LandingPage';
import WelcomeGuide from './components/WelcomeGuide';
import KeyboardShortcuts from './components/KeyboardShortcuts';
import ImportModal from './components/ImportModal';
import CloudKeyModal from './components/CloudKeyModal';
import EmailVerificationBanner from './components/EmailVerificationBanner';

// Styles
import './App.css';

// Set up Modal
Modal.setAppElement('#root');

function App() {
  const { theme, themes } = useTheme();
  const [selectedCategory, setSelectedCategory] = useState('All Notes');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFilter, setSearchFilter] = useState('title');
  const [notes, setNotes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showTagManager, setShowTagManager] = useState(false);
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState([]);
  const [createNoteTrigger, setCreateNoteTrigger] = useState(false);
  const [showWelcomeGuide, setShowWelcomeGuide] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [user, loading] = useAuthState(auth);
  const searchInputRef = useRef(null);
  const currentNotesUserIdRef = useRef(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [signInProvider, setSignInProvider] = useState(null);
  const [isEmailVerified, setIsEmailVerified] = useState(null);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const mobileSidebarButtonRef = useRef(null);

  useEffect(() => {
    let isCurrent = true;
    let tokenRequest = 0;
    setSignInProvider(null);
    setIsEmailVerified(null);

    const unsubscribe = onIdTokenChanged(auth, async (tokenUser) => {
      const requestId = ++tokenRequest;
      setSignInProvider(null);
      setIsEmailVerified(null);

      if (!tokenUser?.getIdTokenResult) {
        return;
      }

      try {
        const tokenResult = await tokenUser.getIdTokenResult();
        if (isCurrent && requestId === tokenRequest) {
          setSignInProvider(tokenResult.claims?.firebase?.sign_in_provider || null);
          setIsEmailVerified(tokenResult.claims?.email_verified === true);
        }
      } catch (error) {
        logWarning('Unable to determine sign-in provider', error);
        if (isCurrent && requestId === tokenRequest) {
          setSignInProvider(null);
          setIsEmailVerified(null);
        }
      }
    });

    return () => {
      isCurrent = false;
      unsubscribe();
    };
  }, []);

  // Subscribe to user's notes
  useEffect(() => {
    let unsubscribe = () => {};
    let isActive = true;
    const userId = user?.uid || null;

    if (currentNotesUserIdRef.current !== userId) {
      setNotes([]);
      currentNotesUserIdRef.current = userId;
    }

    if (user) {
      unsubscribe = subscribeToNotes(user.uid, (updatedNotes) => {
        if (!isActive || currentNotesUserIdRef.current !== userId) {
          return;
        }
        log('Received updated notes');
        setNotes(updatedNotes || []);
      });
    }

    return () => {
      isActive = false;
      unsubscribe();
    };
  }, [user]);

  // Subscribe to user's tags
  useEffect(() => {
    let unsubscribe = () => {};

    if (user) {
      unsubscribe = subscribeToUserTags(user.uid, (updatedTags) => {
        log('Received updated tags');
        setTags(updatedTags || []);
      });
    } else {
      setTags([]);
    }

    return () => unsubscribe();
  }, [user]);

  // Subscribe to user's categories
  useEffect(() => {
    let unsubscribe = () => {};

    if (user) {
      unsubscribe = subscribeToUserCategories(user.uid, (updatedCategories) => {
        log('Received updated categories');
        setCategories(updatedCategories || []);
      });
    } else {
      setCategories([]);
    }

    return () => unsubscribe();
  }, [user]);

  const handleUpdateCategories = async (newCategories) => {
    if (user) {
      try {
        await updateUserCategories(user.uid, newCategories);
      } catch (error) {
        logError('Error updating categories', error);
      }
    }
  };

  const handleTagSelect = (tag) => {
    if (tag === null) {
      setSelectedTag([]);
    } else {
      setSelectedTag(prev => {
        if (prev.includes(tag)) {
          return prev.filter(t => t !== tag);
        } else {
          return [...prev, tag];
        }
      });
    }
  };

  const handleCategorySelect = (categoryName) => {
    if (selectedCategory === categoryName) {
      setSelectedCategory('All Notes');
    } else {
      setSelectedCategory(categoryName);
    }
  };

  const handleMobileSidebarSelection = () => {
    setIsMobileSidebarOpen(false);
    if (isMobileSidebarOpen) {
      mobileSidebarButtonRef.current?.focus();
    }
  };

  useEffect(() => {
    if (!isMobileSidebarOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsMobileSidebarOpen(false);
        mobileSidebarButtonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMobileSidebarOpen]);

  // Handle new user setup and welcome guide
  useEffect(() => {
    const setupNewUser = async () => {
      if (user) {
        try {
          log('Attempting to initialize user');
          // Wait for categories and tags subscriptions to be set up
          await new Promise(resolve => setTimeout(resolve, 1000));
          const isNewUser = await initializeNewUser(user.uid);
          
          // Show welcome guide for new users
          if (isNewUser) {
            setShowWelcomeGuide(true);
          }
          
        } catch (error) {
          logError('Error setting up new user', error);
        }
      }
    };

    if (user && !loading) {
      setupNewUser();
    }
  }, [user, loading]);

  // Check if user is new and show welcome guide
  useEffect(() => {
    const checkNewUser = async () => {
      if (user) {
        const isNewUser = await initializeNewUser(user.uid);
        if (isNewUser) {
          setShowWelcomeGuide(true);
        } else {
          setShowWelcomeGuide(false);
        }
      }
    };
    checkNewUser();
  }, [user]);

  const handleCloseWelcomeGuide = () => {
    setShowWelcomeGuide(false);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSearchFilter('title');
    setSelectedTag([]);
    setSelectedCategory('All Notes');
  };

  // Add keyboard shortcut handlers
  const handleCreateNote = () => {
    setCreateNoteTrigger(true);
  };

  const handleFocusSearch = () => {
    searchInputRef.current?.focus();
  };

  const handleSaveNote = () => {
    if (selectedNote) {
      log('Attempting to save note');
      // Try to find the save button using multiple strategies
      const saveButton = 
        document.querySelector('button.note-save-button') ||  // Primary selector
        document.querySelector('button.bg-blue-500') ||  // Backup selector
        Array.from(document.querySelectorAll('button')).find(button => {
          const text = button.textContent.trim().toLowerCase();
          return text === 'save';
        });
      
      if (saveButton) {
        log('Save button found');
        saveButton.click();
        return true;
      }

      logWarning('Save button not found');
      return false;
    }
    logWarning('No note selected for saving');
    return false;
  };

  const handleCloseModal = () => {
    if (selectedNote) {
      setSelectedNote(null);
    }
  };

  const handleExport = async (format, selectedNotes, encryptionPolicy) => {
    log('Starting export process');
    const notesToExport = selectedNotes || notes;
    
    if (!notesToExport || notesToExport.length === 0) {
      log('No notes to export');
      return;
    }

    log(`Processing ${notesToExport.length} notes for export`);

    try {
      if (format === 'markdown') {
        const markdownContent = notesToExport.some((note) => note.protection === 'vault')
          ? serializeNotesToMarkdownV3(notesToExport, noteHtmlToMarkdown)
          : serializeNotesToMarkdownV2(notesToExport, noteHtmlToMarkdown, undefined, encryptionPolicy);

        log('Created markdown content, attempting to save');
        const element = document.createElement('a');
        element.setAttribute('href', 'data:text/markdown;charset=utf-8,' + encodeURIComponent(markdownContent));
        element.setAttribute('download', `my_notes_${new Date().toISOString().split('T')[0]}.md`);
        element.style.display = 'none';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
        log('Markdown file download initiated');
      } 
      else if (format === 'pdf') {
        const plaintextNotes = notesToExport.filter((note) => note.protection !== 'vault');
        if (plaintextNotes.length === 0) return;
        log('Starting PDF generation with html2pdf');
        
        // Create a temporary container for the HTML content
        const container = document.createElement('div');
        container.style.padding = '20px';
        container.style.fontFamily = 'Roboto, sans-serif';
        container.style.color = '#000000'; // Ensure dark text color

        // Add each note's content to the container
        plaintextNotes.forEach((note, index) => {
          const noteDiv = document.createElement('div');
          noteDiv.style.marginBottom = '30px';
          noteDiv.style.pageBreakAfter = index < plaintextNotes.length - 1 ? 'always' : 'auto';
          noteDiv.style.position = 'relative'; // Enable proper positioning

          // Add title
          const title = document.createElement('h1');
          title.textContent = note.title;
          title.style.fontSize = '24px';
          title.style.marginBottom = '10px';
          title.style.color = '#000000';
          title.style.fontWeight = 'bold';
          noteDiv.appendChild(title);

          // Add metadata (category and tags)
          if (note.category || (note.tags && note.tags.length)) {
            const metadata = document.createElement('div');
            metadata.style.marginBottom = '15px';
            metadata.style.color = '#333333';
            metadata.style.fontSize = '14px';

            if (note.category) {
              const category = document.createElement('div');
              category.textContent = `Category: ${note.category}`;
              metadata.appendChild(category);
            }

            if (note.tags && note.tags.length) {
              const tags = document.createElement('div');
              tags.textContent = `Tags: ${note.tags.join(', ')}`;
              metadata.appendChild(tags);
            }

            noteDiv.appendChild(metadata);
          }

          // Add content with proper styling
          const content = document.createElement('div');
          content.innerHTML = sanitizeNoteHtml(note.content);
          content.style.color = '#000000';
          content.style.lineHeight = '1.6';
          content.style.position = 'relative';
          content.style.zIndex = '1';
          
          // Style all images within the content
          const images = content.getElementsByTagName('img');
          Array.from(images).forEach(img => {
            img.style.display = 'block';
            img.style.maxWidth = '90%';
            img.style.height = 'auto';
            img.style.marginTop = '20px';
            img.style.marginBottom = '20px';
            img.style.marginLeft = 'auto';
            img.style.marginRight = 'auto';
            img.style.pageBreakInside = 'avoid';
          });

          // Style all paragraphs and text elements
          const paragraphs = content.getElementsByTagName('p');
          Array.from(paragraphs).forEach(p => {
            p.style.color = '#000000';
            p.style.marginBottom = '10px';
            p.style.position = 'relative';
          });

          // Style lists and list items
          const lists = content.querySelectorAll('ul, ol');
          Array.from(lists).forEach(list => {
            list.style.paddingLeft = '40px';
            list.style.marginBottom = '15px';
            list.style.marginTop = '15px';
            list.style.position = 'relative';
            
            if (list.tagName === 'UL') {
              list.style.listStyle = 'disc';
            } else if (list.tagName === 'OL') {
              list.style.listStyle = 'decimal';
            }
          });

          const listItems = content.getElementsByTagName('li');
          Array.from(listItems).forEach((li, index) => {
            li.style.color = '#000000';
            li.style.marginBottom = '8px';
            li.style.paddingLeft = '5px';
            
            // Fix the alignment by using flexbox
            li.style.display = 'flex';
            li.style.alignItems = 'flex-start';
            
            // Create a marker for the bullet/number
            const marker = document.createElement('span');
            marker.style.minWidth = '25px';
            marker.style.marginLeft = '-25px';
            marker.style.marginRight = '5px';
            
            if (li.parentElement.tagName === 'OL') {
              marker.textContent = `${index + 1}.`;
              li.style.listStyle = 'none';
            } else {
              marker.textContent = '•';
              li.style.listStyle = 'none';
            }
            
            // Wrap the content
            const content = document.createElement('span');
            content.innerHTML = li.innerHTML;
            li.innerHTML = '';
            
            li.appendChild(marker);
            li.appendChild(content);
          });

          noteDiv.appendChild(content);
          container.appendChild(noteDiv);
        });

        // Configure html2pdf options
        const opt = {
          margin: [15, 15],
          filename: `my_notes_${new Date().toISOString().split('T')[0]}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { 
            scale: 2,
            useCORS: true,
            logging: true,
            backgroundColor: '#ffffff'
          },
          jsPDF: { 
            unit: 'mm', 
            format: 'a4', 
            orientation: 'portrait',
            compress: true
          }
        };

        // Generate PDF
        html2pdf().set(opt).from(container).save()
          .then(() => {
            log('PDF generation completed');
          })
          .catch(error => {
            logError('Error generating PDF', error);
          });
      }
    } catch (error) {
      logError('Error in export process', error);
    }
  };

  const handleImport = async (importedNotes) => {
    if (!user) throw new Error('You must be signed in to import notes.');
    
    try {
      const startingOrder = notes.reduce((highestOrder, note) => Math.max(highestOrder, note.order || 0), 0);
      await importNotesAtomically(user.uid, importedNotes, startingOrder);
    } catch (error) {
      logError('Error importing notes', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${themes[theme].background} flex items-center justify-center`} style={{ backgroundImage: themes[theme].backgroundImage }}>
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-white"></div>
      </div>
    );
  }

  if (!user) {
    return <LandingPage />;
  }

  const needsEmailVerification = signInProvider === 'password' && isEmailVerified !== true;

  return (
    <EncryptionProvider key={user.uid} userId={user.uid}>
      <div className={`min-h-screen ${themes[theme].background} ${themes[theme].mode === 'dark' ? 'dark' : ''} flex flex-col relative`} style={{ backgroundImage: themes[theme].backgroundImage }}>
        <KeyboardShortcuts
          onCreateNote={handleCreateNote}
          onFocusSearch={handleFocusSearch}
          onSaveNote={handleSaveNote}
          onCloseModal={handleCloseModal}
          isModalOpen={!!selectedNote}
        />
        <Header
          onSearchChange={setSearchQuery}
          searchQuery={searchQuery}
          searchFilter={searchFilter}
          setSearchFilter={setSearchFilter}
          onClearFilters={handleClearFilters}
          triggerNewNote={() => setCreateNoteTrigger(true)}
          setSelectedTag={setSelectedTag}
          setSelectedCategory={setSelectedCategory}
          searchInputRef={searchInputRef}
          onExport={handleExport}
          onImport={() => setShowImportModal(true)}
          notes={notes}
        />
        {needsEmailVerification && <EmailVerificationBanner user={user} />}
        <div className="flex flex-1 flex-col overflow-hidden md:flex-row">
          <div className="px-4 pt-2 md:hidden">
            <button
              ref={mobileSidebarButtonRef}
              type="button"
              aria-expanded={isMobileSidebarOpen}
              aria-controls="mobile-categories-panel"
              onClick={() => setIsMobileSidebarOpen((isOpen) => !isOpen)}
              className="w-full rounded border border-blue-300/50 bg-white/10 px-4 py-2 text-left text-sm font-semibold text-current hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-yellow-400"
            >
              Categories &amp; tags
            </button>
          </div>
          <Sidebar
            categories={categories}
            setCategories={handleUpdateCategories}
            onCategorySelect={handleCategorySelect}
            notes={notes}
            setNotes={setNotes}
            tags={tags}
            setShowTagManager={setShowTagManager}
            onTagSelect={handleTagSelect}
            selectedCategory={selectedCategory}
            selectedTag={selectedTag}
            onMobileSelection={handleMobileSidebarSelection}
            className={`${isMobileSidebarOpen ? 'flex max-h-[65vh] overflow-hidden' : 'hidden'} md:flex md:max-h-none md:overflow-visible`}
            id="mobile-categories-panel"
          />
          <ContentArea
            createNoteTrigger={createNoteTrigger}
            setCreateNoteTrigger={setCreateNoteTrigger}
            selectedCategory={selectedCategory}
            searchQuery={searchQuery}
            searchFilter={searchFilter}
            categories={categories}
            notes={notes}
            setNotes={setNotes}
            selectedTag={selectedTag}
            tags={tags}
            selectedNote={selectedNote}
            setSelectedNote={setSelectedNote}
            onExport={handleExport}
            onClearFilters={handleClearFilters}
          />
        </div>
        
        <Footer />
        <CookieConsent />
        {showTagManager && (
          <TagManager
            tags={tags}
            setTags={setTags}
            setShowTagManager={setShowTagManager}
            userId={user?.uid}
          />
        )}
        {showWelcomeGuide && (
          <WelcomeGuide onClose={handleCloseWelcomeGuide} />
        )}
        <ImportModal
          isOpen={showImportModal}
          onRequestClose={() => setShowImportModal(false)}
          onImport={handleImport}
        />
        <CloudKeyModal />
      </div>
    </EncryptionProvider>
  );
}

export default App;
